// Display formatting: the only place units and numbers are turned into text.

import type { Field } from '@/api';

const UNIT_LABELS: Record<string, string> = {
	Cel: '°C',
	'%RH': '% RH',
	'ug/m3': 'µg/m³',
	m3: 'm³',
	'm3/h': 'm³/h',
	'kW.h': 'kWh',
};

export const unitLabel = (unit: string) => UNIT_LABELS[unit] ?? unit;

export function formatValue(field: Pick<Field, 'unit' | 'decimals' | 'kind'>, v: unknown): string {
	if (v === undefined || v === null) return '—';
	if (typeof v === 'boolean') return v ? 'on' : 'off';
	if (typeof v !== 'number') return String(v);
	const n = v.toLocaleString(undefined, { minimumFractionDigits: field.decimals, maximumFractionDigits: field.decimals });
	const u = unitLabel(field.unit);
	return u ? `${n} ${u}` : n;
}

export function formatInterval(ms: number): string {
	if (ms < 1000) return `${ms} ms`;
	if (ms < 60_000) return `${+(ms / 1000).toFixed(1)} s`;
	if (ms < 3_600_000) return `${+(ms / 60_000).toFixed(1)} min`;
	return `${+(ms / 3_600_000).toFixed(1)} h`;
}

export function timeAgo(iso: string, now = Date.now()): string {
	const s = Math.max(0, Math.round((now - Date.parse(iso)) / 1000));
	if (s < 5) return 'just now';
	if (s < 60) return `${s}s ago`;
	if (s < 3600) return `${Math.round(s / 60)}m ago`;
	if (s < 86_400) return `${Math.round(s / 3600)}h ago`;
	return `${Math.round(s / 86_400)}d ago`;
}

export const PROTOCOL_LABELS: Record<string, string> = {
	mqtt: 'MQTT',
	modbus_tcp: 'Modbus TCP',
	opc_ua: 'OPC UA',
	coap: 'CoAP',
	http: 'HTTP',
	ambient: 'slims-ambient',
};

/** `mean_dwell_s` / `meanDwellS` / `random_walk` → "Mean dwell s" / "Random walk". */
export function humanize(key: string): string {
	const words = key
		.replace(/([a-z0-9])([A-Z])/g, '$1 $2')
		.replace(/[_.]/g, ' ')
		.trim()
		.toLowerCase();
	return words.charAt(0).toUpperCase() + words.slice(1);
}
